<template>
  <BaseDialog
    contentClass="category"
    ref="dialog"
    :show-actions="true"
    :persistent="isDirty"
    @closeModal="closeDialog"
    sidePanel
  >
    <template slot="title">{{ $t('page.admin-keywords.new-category') }}</template>
    <template slot="content">
      <form @submit.prevent="onSubmit" novalidate class="new-category__form">
        <div class="login-form__section-small">
          <FormInput
            :label="$t('page.admin-keywords.category-name')"
            @input="onNameChange"
            has-gray-background
            name="categoryName"
            type="name"
            isRequired
            ref="categoryName"
            v-model="categoryName"
            v-validate="'required'"
          />
        </div>
        <FormColorPicker
          v-if="hasColorChoice"
          :isRequired="true"
          v-model="categoryColor"
          :label="$t('page.admin-opportunity-management.add-dialog.color')"
        ></FormColorPicker>
        <div v-if="!hasColorChoice" class="preferences__locale">
          <div class="preferences__label">
            <FormLabel>{{ $t('page.admin-user-profile.language') }}</FormLabel>
          </div>
          <div class="preferences__dropdown">
            <LocaleProvider ref="localeProvider">
              <template slot-scope="{ currentLocale, locales }">
                <BaseSelect
                  name="locale"
                  :value="currentLocale"
                  :v-model="categoryLocale"
                  :items="locales"
                  item-text="name"
                  item-value="locale"
                  :hint="$t('page.admin-user-profile.language-hint')"
                  @change="onLocaleChange"
                />
              </template>
            </LocaleProvider>
          </div>
        </div>
        <div class="actions">
          <FormButton
            is-large
            :disabled="isFormInvalid"
            :is-outlined="isFormInvalid"
            type="submit"
            class="new-category__submit"
          >
            {{ $t('page.admin-keywords.new-category') }}
          </FormButton>
        </div>
      </form>
    </template>
  </BaseDialog>
</template>

<script>
import FormButton from '@/components/form/FormButton';
import BaseDialog from '@/components/base/BaseDialog';
import FormInput from '@/components/form/FormInput.vue';
import FormLabel from '@/components/form/FormLabel.vue';
import LocaleProvider from '@/components/locale/LocaleProvider';
import BaseSelect from '@/components/base/BaseSelect';
import FormColorPicker from '../form/FormColorPicker.vue';

import { mapActions, mapGetters } from 'vuex';

export default {
  name: 'AddCategoryDialog',
  components: {
    FormButton,
    BaseDialog,
    BaseSelect,
    FormInput,
    FormLabel,
    LocaleProvider,
    FormColorPicker,
  },
  data() {
    return {
      isDirty: false,
      categoryName: '',
      categoryLocale: '',
      categoryColor: '',
    };
  },
  props: {
    hasColorChoice: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    ...mapGetters('documentKeywords', ['categories']),
    isFormInvalid() {
      return !!!this.categoryName;
    },
    currentLocale() {
      return this.$i18n.locale;
    },
  },
  methods: {
    ...mapActions('keywords', ['addCategory']),
    ...mapActions('documentKeywords', ['addCategoryDoc', 'getKeywords']),
    openDialog() {
      this.categoryName = '';
      this.categoryColor = '';
      this.$refs.dialog.openModal();

      this.$nextTick(() => {
        const refInput = this.$refs.categoryName.$el.querySelector('input');
        if (refInput) {
          this.$nextTick(() => {
            refInput.focus();
          });
        }
      });
    },
    closeDialog() {
      this.isDirty = false;
      this.$refs.dialog.isModalOpen = false;
    },
    onNameChange() {
      this.isDirty = true;
    },
    onLocaleChange(locale) {
      this.isDirty = true;
      this.categoryLocale = locale;
    },
    onSubmit() {
      if (this.isCategoryNameExists()) {
        return;
      }
      if (!this.hasColorChoice) {
        const locale = this.categoryLocale.split('-')[0];
        this.addCategory({ name: this.categoryName, language: locale })
          .then(() => {
            this.$toast.success(this.$t('page.admin-keywords.new-category-created'));
            this.$emit('action-succeeded');
          })
          .catch((error) => this.$toast.error(error));
        this.isDirty = false;
        this.closeDialog();
      } else {
        this.addCategoryDoc({ language: 'fr', name: this.categoryName, colorCode: this.categoryColor })
          .then(() => {
            this.$toast.success(this.$t('page.admin-keywords.new-category-created'));
            this.$emit('action-succeeded');
          })
          .catch((error) => this.$toast.error(error));
      }
      this.closeDialog();
    },
    isCategoryNameExists() {
      if (Array.isArray(this.categories)) {
        for (let i = 0; i < this.categories.length; i++) {
          const category = this.categories[i];
          if (category.name.toLowerCase() === this.categoryName.trim().toLowerCase()) {
            return true;
          }
        }
      }
      return false;
    },
  },
  mounted() {
    // Initializing these values ​​the first time
    this.categoryLocale = this.currentLocale;
  },
  watch: {
    currentLocale() {
      this.categoryLocale = this.currentLocale;
    },
  },
};
</script>

<style lang="scss">
.base-dialog__category {
  .base-dialog__content {
    padding: 32px;
    background: $color-white !important;
  }

  .actions {
    display: flex;
    justify-content: center;
    padding: 2rem;
  }
}
</style>
